import React, {useEffect, useRef} from "react";
import {useLocation} from "react-router-dom";
import {cleanUrl, gtag, isPageLocationPinned, trackEvent} from "./analytics";

export const VIRTUAL_PAGE_VIEW_EVENT = "virtual_page_view";

// Runs the callback after the next paint, when react-helmet-async has already
// applied the new document.title.
function afterNextPaint(callback: () => void): () => void {
    let timeout: number | undefined;
    if (typeof window.requestAnimationFrame !== "function") {
        timeout = window.setTimeout(callback, 0);
        return () => window.clearTimeout(timeout);
    }
    const frame = window.requestAnimationFrame(() => {
        timeout = window.setTimeout(callback, 0);
    });
    return () => {
        window.cancelAnimationFrame(frame);
        window.clearTimeout(timeout);
    };
}

// Pushes `virtual_page_view` once per screen, after the new document.title is
// applied: navigation_type "load" for the first screen of a page load and
// "route_change" for in-app navigation (history.pushState via react-router).
//
// The event only feeds GTM; it sends nothing to GA4 by itself. Two valid GTM
// setups, never both at once (or page views double):
//  A) default: the Google tag's automatic page_view + GA4 enhanced measurement
//     "page changes based on browser history events" (on for G-9KYJW71J59).
//     Leave this event unmapped.
//  B) send_page_view=false on the Google tag, history events off in the GA4
//     stream, and a GA4 event tag "page_view" on this event with page_location
//     / page_title from it. Gives correct titles on the first screen and
//     session ids already removed from the URL.
const AnalyticsRouteListener: React.FC = () => {
    const location = useLocation();
    const lastTracked = useRef<string | null>(null);
    const lastLocation = useRef<string>(cleanUrl(window.location.href));

    useEffect(() => {
        const key = location.pathname + location.search;
        if (lastTracked.current === key) {
            return;
        }
        const navigationType = lastTracked.current === null ? "load" : "route_change";
        if (navigationType === "route_change" && isPageLocationPinned()) {
            // A pinned page_location would otherwise stick to every later page
            // of this window; keep it (and the referrer) current and sanitised.
            gtag("set", {page_location: cleanUrl(window.location.href), page_referrer: lastLocation.current});
        }
        return afterNextPaint(() => {
            lastTracked.current = key;
            const pageLocation = cleanUrl(window.location.href);
            lastLocation.current = pageLocation;
            trackEvent(VIRTUAL_PAGE_VIEW_EVENT, {
                navigation_type: navigationType,
                page_location: pageLocation,
                page_path: pageLocation ? new URL(pageLocation).pathname : "",
                page_title: document.title,
            });
        });
    }, [location.pathname, location.search]);

    return null;
};

export default AnalyticsRouteListener;
